import isFinite from 'lodash/isFinite';
import isNumber from 'lodash/isNumber';
import get from 'lodash/get';
import moment from 'moment';

import { ValueMap, ValueFormatter } from '../../@types';
import { formatDuration } from '../momentUtils';
import { getRangeMatcher } from './range';

/**
 * I18n number formatter
 */
export const NUMBER_FORMAT = new Intl.NumberFormat('pt-BR');

export interface FormatNumberOptions
  extends Omit<
    Intl.NumberFormatOptions,
    'minimumFractionDigits' | 'maximumFractionDigits'
  > {
  precision?: number;
}

/**
 * Formats a number to pt-BR i18n format
 * @param value
 * @returns
 */
export const formatNumber = (
  value: number | string,
  options?: FormatNumberOptions,
) => {
  if (!isNumber(Number(value))) {
    return value;
  }
  const numberFormat = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: options?.precision,
    maximumFractionDigits: options?.precision,
    ...options,
  });
  return numberFormat.format(Number(value));
};

/**
 * Data Format/Mappings
 */

export type DataFormatter = (
  value: string | number | boolean,
  formatter?: ValueFormatter,
  mapping?: Array<ValueMap>,
) => string | number;

/**
 * Applies format configuration
 */
const applyFormatter = (
  value: string | number | object,
  formatter?: ValueFormatter,
): string | number | object => {
  // Let undefined or null cells blank
  if (!formatter || value === undefined || value === 'null') return value;

  if (formatter.type === 'timestamp') {
    return moment(value).format(formatter.format);
  }

  if (formatter.type === 'duration') {
    const duration = moment.duration(value, formatter.unit);
    return formatDuration(duration, formatter.format);
  }

  if (formatter.type === 'percentage') {
    if (!isFinite(Number(value))) return value;
    return `${formatNumber(Number(value) * 100, {
      precision: formatter.precision || 0,
    })}%`;
  }

  if (formatter.type === 'integer') {
    if (!isFinite(Number(value))) return value;
    return Number(value).toFixed();
  }

  if (formatter.type === 'decimal') {
    if (!isFinite(Number(value))) return value;
    return formatNumber(Number(value), { precision: formatter.precision || 0 });
  }

  if (formatter.type === 'number-like') {
    if (!isFinite(Number(value))) return value;
    return formatNumber(Number(value), { precision: formatter.precision || 0 });
  }

  if (formatter.type === 'currency') {
    if (!isFinite(Number(value))) return value;
    return !formatter.hideCurrency
      ? formatNumber(`${Number(value) / 100}`, {
          precision: 2,
          style: 'currency',
          currency: formatter.currency || 'BRL',
        })
      : Number(value) / 100;
  }

  if (formatter.type === 'string') {
    switch (formatter.operation) {
      case 'prepend':
        return `${formatter.value}${value}`;
      case 'append':
        return `${value}${formatter.value}`;
      case 'replace':
        return `${value}`.replace(formatter.substr || '', formatter.value);
      default:
        return value;
    }
  }

  if (formatter.type === 'object') {
    let parsedValue = value;

    if (typeof parsedValue === 'string') {
      try {
        parsedValue = JSON.parse(parsedValue);
      } catch {
        return '-';
      }
    }

    if (
      typeof parsedValue === 'object' &&
      parsedValue !== null &&
      'nodes' in parsedValue
    ) {
      parsedValue = (parsedValue as unknown as { nodes: unknown[] }).nodes;
    }

    if (Array.isArray(parsedValue)) {
      const formatted = parsedValue.map((item) => {
        const key = get(item, formatter.keyPath);
        const val = get(item, formatter.valuePath);

        try {
          const parsed = JSON.parse(val);
          if (parsed?.amount !== undefined) {
            return `${key}: ${parsed.amount}`;
          }
        } catch (error: unknown) {
          if (error instanceof Error) {
            // eslint-disable-next-line no-console
            console.error('Erro ao fazer JSON.parse:', error.message);
          } else {
            // eslint-disable-next-line no-console
            console.error('Erro desconhecido ao fazer JSON.parse');
          }
        }

        return key !== undefined && val !== undefined ? `${key}: ${val}` : '-';
      });

      return formatted.length ? formatted.join('; ') : '-';
    }
  }

  return value;
};

/**
 * Applies mapping configuration
 */
const applyMapping = (
  value: string | number,
  mapping?: Array<ValueMap>,
): string | number => {
  if (!mapping) return value;

  const mapEntry = mapping.find((m) => {
    const match = m.range
      ? getRangeMatcher(m.range, m.rangeType || 'integer')
      : (v: string | number) => v === m.value;
    return match(value);
  });
  if (!mapEntry) return value;

  return mapEntry.label;
};

/**
 * Whether a formatted value is one of the `mapping` labels, i.e. the mapping
 * replaced the value with a label of its own.
 *
 * Callers that decorate the formatted output — `compareTo: { as: 'signs' }`
 * cells prefix it with a `+`/`-` — use this to skip the decoration. The sign is
 * derived from the raw numeric value while the body goes through the formatter
 * and the mapping, so the two can disagree: a sub-second difference formatted
 * as `00:00:00` and mapped to `-` would render as `+-`, a sign with no number.
 */
export const isMappedLabel = (
  formattedValue: string | number,
  mapping?: Array<ValueMap>,
): boolean => !!mapping?.some((m) => m.label === formattedValue);

/**
 * Parses a data mapping
 */
export const formatValue: DataFormatter = (
  value: string | number | boolean,
  formatter?: ValueFormatter,
  mapping?: Array<ValueMap>,
): string | number => {
  const result = applyFormatter(
    typeof value === 'boolean' ? String(value) : value,
    formatter,
  );
  return applyMapping(
    typeof result === 'object' ? JSON.stringify(result) : result,
    mapping,
  );
};
